import { useEffect, useState } from 'react';
import supabase from '@/supabase';
import { useSession } from '@/hooks/useSessionHook';
import {
  DEFAULT_ADMIN_RUNTIME_CONFIG,
  normalizeAdminRuntimeConfig,
  type AdminRuntimeConfig,
} from '@/lib/admin-runtime-config';

export function useAdminRuntimeConfig() {
  const { session, profile } = useSession();
  const [config, setConfig] = useState<AdminRuntimeConfig>(DEFAULT_ADMIN_RUNTIME_CONFIG);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const userId = session?.user?.id;
    const isAdmin = profile?.app_role === 'admin';

    if (!userId || !isAdmin) {
      setConfig(DEFAULT_ADMIN_RUNTIME_CONFIG);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadConfig = async () => {
      setIsLoading(true);

      const { data, error } = await supabase
        .from('user_settings')
        .select('admin_settings')
        .eq('user_id', userId)
        .maybeSingle();

      if (!cancelled) {
        if (error) {
          console.error('Failed to load admin runtime config:', error);
          setConfig(DEFAULT_ADMIN_RUNTIME_CONFIG);
        } else {
          setConfig(normalizeAdminRuntimeConfig((data?.admin_settings ?? null) as Partial<AdminRuntimeConfig> | null));
        }
        setIsLoading(false);
      }
    };

    void loadConfig();

    return () => {
      cancelled = true;
    };
  }, [profile?.app_role, session?.user?.id]);

  return { config, isLoading };
}
